import { PortfolioItem } from 'portfolio/PortfolioItem';
import {
    MyPortfolioCapsulesDataStateAggregation,
    MyPortfolioCapsulesSymbolDataDerivedValues,
    MyPortfolioCapsulesSymbolDataState,
    MyPortfolioCapsulesSymbolDataWeights,
} from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataState';
import { MyPortfolioCapsulesWidgetConfigState } from 'widgets/myPortfolioCapsules/store/config/MyPortfolioCapsulesWidgetConfigState';
import { MyPortfolioCapsulesDataItem } from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesData';
import { Topic } from 'widgets/myPortfolioCapsules/topics/Topic';

export interface CostTopicOptions {
    costField: 'arrivalPSlip' | 'exCost';
    valueField: keyof MyPortfolioCapsulesSymbolDataDerivedValues;
    absValueField: keyof MyPortfolioCapsulesSymbolDataDerivedValues;
}

export type CommonCostTopicFields = Pick<
    Topic,
    | 'requiredPortfolioColumns'
    | 'accumulatedColumns'
    | 'weights'
    | 'calculateDerivedValues'
    | 'getSymbolValues'
    | 'options'
>;

export const costTopic: (options: CostTopicOptions) => CommonCostTopicFields = (
    options
) => {
    const { costField, valueField, absValueField } = options;

    const costFieldCol = costField === 'exCost' ? 'estCost' : costField;

    return {
        requiredPortfolioColumns: [costFieldCol, 'averagePrice'],
        accumulatedColumns: [],
        weights: ['tradedQtyWeightedByAveragePrice'],
        calculateDerivedValues,
        getSymbolValues,
        options: { absolute: true },
    };

    function calculateDerivedValues(
        item: PortfolioItem,
        symbolDataState: MyPortfolioCapsulesSymbolDataState,
        itemWeights: MyPortfolioCapsulesSymbolDataWeights,
        _factor: 1 | -1
    ) {
        const itemCostWeightedByTradedValue =
            (item[costFieldCol] ?? 0) *
            itemWeights.tradedQtyWeightedByAveragePrice!;

        // Accumulate the ADV% weighted by PNC/qty by symbol...
        symbolDataState.derived[valueField] =
            (symbolDataState.derived[valueField] ?? 0) +
            itemCostWeightedByTradedValue;

        const itemCostAbsWeightedByTradedValue =
            Math.abs(item[costFieldCol] ?? 0) *
            itemWeights.tradedQtyWeightedByAveragePrice!;

        symbolDataState.derived[absValueField] =
            (symbolDataState.derived[absValueField] ?? 0) +
            itemCostAbsWeightedByTradedValue;
    }

    /*
     * Given aggregated data, calculates the values per symbol
     */
    function getSymbolValues(
        aggregatedData: MyPortfolioCapsulesDataStateAggregation,
        config: MyPortfolioCapsulesWidgetConfigState,
        exchangeRate: number
    ): MyPortfolioCapsulesDataItem[] {
        const { absoluteValues } = config;

        return Object.keys(aggregatedData.bySymbol)
            .map((symbol) => ({
                symbol,
                symbolData: aggregatedData.bySymbol[symbol],
            }))
            .map((data) => {
                const { symbol, symbolData } = data;

                const {
                    derived,
                    weights: { tradedQtyWeightedByAveragePrice },
                } = symbolData;

                const derValue = derived[valueField];
                const derAbsValue = derived[absValueField];

                const itemValue =
                    (absoluteValues ? derAbsValue : derValue) ?? 0;

                // Over the total weight of the symbol...
                const value = tradedQtyWeightedByAveragePrice
                    ? itemValue / tradedQtyWeightedByAveragePrice
                    : 0;

                const usersValue = value * exchangeRate;

                return {
                    symbol,
                    qty: symbolData.accumulated.qty,
                    qtyTraded: symbolData.accumulated.qtyTraded,
                    qtyRemaining: symbolData.accumulated.qtyRemaining,
                    value: usersValue,
                };
            });
    }
};
